import React, { useState, useEffect } from 'react';
import { gmDisplayName } from '../utils/displayName';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const TradeManagement = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [receivingTeams, setReceivingTeams] = useState([]); // Teams with GMs for receiving dropdown
  const [teamPlayers, setTeamPlayers] = useState({}); // Players organized by teamId for active season
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Trade creation state
  const [offeringTeamId, setOfferingTeamId] = useState('');
  const [receivingTeamId, setReceivingTeamId] = useState('');
  const [offeredPlayerIds, setOfferedPlayerIds] = useState([]);
  const [requestedPlayerIds, setRequestedPlayerIds] = useState([]);
  const [tradeMessage, setTradeMessage] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [validation, setValidation] = useState(null);

  // 3-Team-Trade: drittes Team plus Zielteam je ausgewähltem Spieler.
  // thirdTeamEnabled === false bedeutet klassischer 2-Team-Trade. Das Flag ist
  // getrennt von thirdTeamId, weil der Slot schon offen ist, bevor der GM das
  // dritte Team gewählt hat.
  const [thirdTeamEnabled, setThirdTeamEnabled] = useState(false);
  const [thirdTeamId, setThirdTeamId] = useState('');
  const [thirdTeamPlayerIds, setThirdTeamPlayerIds] = useState([]);
  const [playerDestinations, setPlayerDestinations] = useState({});
  const [threeTeamValidation, setThreeTeamValidation] = useState(null);

  // Season trade budget (5 trades / 7 players, configurable per season)
  const [tradeLimits, setTradeLimits] = useState(null);

  // Discord announcement text, keyed by trade id
  const [discordTexts, setDiscordTexts] = useState({});
  const [copiedTradeId, setCopiedTradeId] = useState(null);
  const [discordLoadingId, setDiscordLoadingId] = useState(null);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('pending');
  const [activeTab, setActiveTab] = useState('create'); // 'create', 'pending', or 'history'

  useEffect(() => {
    fetchTeams();
    fetchTeamsWithGMs();
    fetchTradeLimits();
  }, []);

  // Fetch team players when teams are loaded
  useEffect(() => {
    if (teams.length > 0) {
      fetchAllTeamPlayers();
    }
  }, [teams]);

  // Auto-select user's team when user data is loaded
  useEffect(() => {
    if (user && user.teamId && teams.length > 0) {
      setOfferingTeamId(user.teamId.toString());
    }
  }, [user, teams]);

  useEffect(() => {
    // Reset filter when switching to history tab
    if (activeTab === 'history' && filterStatus === 'pending') {
      setFilterStatus('');
    } else if (activeTab === 'pending' && filterStatus !== 'pending') {
      setFilterStatus('pending');
    }
  }, [activeTab]);

  useEffect(() => {
    fetchTrades();
  }, [filterStatus, activeTab]);

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/teams');
      const data = await response.json();
      setTeams(data);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const fetchTeamsWithGMs = async () => {
    try {
      const response = await fetch('/api/trades/teams-with-gms');
      if (!response.ok) {
        throw new Error('Failed to fetch teams with GMs');
      }
      const data = await response.json();
      setReceivingTeams(data);
    } catch (err) {
      console.error('Error fetching teams with GMs:', err);
      setError('Failed to load teams with GMs. Some features may not work correctly.');
      setReceivingTeams([]); // Fallback to empty array
    }
  };

  const fetchAllTeamPlayers = async () => {
    try {
      const teamPlayersData = {};
      
      // Fetch players for each team using season-aware endpoint
      await Promise.all(teams.map(async (team) => {
        try {
          const response = await fetch(`/api/players/team/${team.id}`);
          if (response.ok) {
            const players = await response.json();
            teamPlayersData[team.id] = players;
          } else {
            teamPlayersData[team.id] = [];
          }
        } catch (err) {
          console.error(`Error loading players for team ${team.name}:`, err);
          teamPlayersData[team.id] = [];
        }
      }));
      
      setTeamPlayers(teamPlayersData);
    } catch (err) {
      console.error('Error fetching team players:', err);
      setError('Failed to load team rosters for active season');
    }
  };

  const fetchTrades = async () => {
    try {
      // For history tab, fetch all non-pending trades. For pending tab, fetch pending only
      let status = filterStatus;
      if (activeTab === 'history' && filterStatus === 'pending') {
        status = ''; // Fetch all statuses
      }
      
      // Include teamId so the user can see their own private trades
      const teamParam = user?.teamId ? `&teamId=${user.teamId}` : '';
      const response = await fetch(`/api/trades?status=${status}${teamParam}`);
      const data = await response.json();
      setTrades(data);
    } catch (err) {
      console.error('Error fetching trades:', err);
    }
  };

  const fetchTradeLimits = async () => {
    try {
      const response = await fetch('/api/trades/limits');
      if (!response.ok) throw new Error('Failed to fetch trade limits');
      const data = await response.json();
      setTradeLimits(data);
    } catch (err) {
      console.error('Error fetching trade limits:', err);
      setTradeLimits(null);
    }
  };

  // --- 3-Team-Trades ------------------------------------------------------
  // Der Trade wird intern immer als Liste von Seiten gefuehrt: jede Seite ist
  // ein Team mit den Spielern, die es *abgibt*. Bei zwei Teams ist das Ziel
  // eindeutig, bei drei Teams traegt jeder Spieler sein Zielteam in
  // playerDestinations. Alle Zaehl-, Pruef- und Absendepfade arbeiten auf
  // dieser Liste, damit es keine Sonderfaelle pro Teamzahl gibt.
  const isThreeTeam = thirdTeamEnabled;

  const tradeSides = [
    {
      key: 'offering',
      teamId: offeringTeamId,
      selectedIds: offeredPlayerIds,
      setSelectedIds: setOfferedPlayerIds,
      accent: 'blue'
    },
    {
      key: 'receiving',
      teamId: receivingTeamId,
      selectedIds: requestedPlayerIds,
      setSelectedIds: setRequestedPlayerIds,
      accent: 'green'
    },
    ...(isThreeTeam && thirdTeamId !== '' ? [{
      key: 'third',
      teamId: thirdTeamId,
      selectedIds: thirdTeamPlayerIds,
      setSelectedIds: setThirdTeamPlayerIds,
      accent: 'purple'
    }] : [])
  ].filter(side => side.teamId !== '');

  const participatingTeamIds = tradeSides.map(side => parseInt(side.teamId, 10));

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === parseInt(teamId, 10));
    return team ? team.name : '';
  };

  // Standard-Ziel beim Auswaehlen: das naechste Team im Ring (A→B, B→C, C→A).
  // Der haeufigste Fall und jederzeit im Dropdown aenderbar.
  const defaultDestinationFor = (sideKey) => {
    const index = tradeSides.findIndex(side => side.key === sideKey);
    if (index === -1 || participatingTeamIds.length < 2) return null;
    return participatingTeamIds[(index + 1) % participatingTeamIds.length];
  };

  const toggleSelection = (side, playerId) => {
    if (side.selectedIds.includes(playerId)) {
      side.setSelectedIds(side.selectedIds.filter(id => id !== playerId));
      setPlayerDestinations(prev => {
        const next = { ...prev };
        delete next[playerId];
        return next;
      });
      return;
    }
    if (side.selectedIds.length >= 4) return;
    side.setSelectedIds([...side.selectedIds, playerId]);
    if (isThreeTeam) {
      const target = defaultDestinationFor(side.key);
      if (target) {
        setPlayerDestinations(prev => ({ ...prev, [playerId]: target }));
      }
    }
  };

  const setDestination = (playerId, targetTeamId) => {
    setPlayerDestinations(prev => ({ ...prev, [playerId]: parseInt(targetTeamId, 10) }));
  };

  /**
   * Teamwechsel auf einer Seite. Zwei Dinge werden ungueltig: die Auswahl
   * dieser Seite (fremder Kader) und jede Ziel-Zuordnung, die auf das alte
   * Team zeigte. Alles andere bleibt stehen, damit ein Wechsel des dritten
   * Teams nicht die ganze Arbeit zerlegt.
   */
  const handleTeamChange = (sideKey, previousTeamId, newTeamId) => {
    const setters = {
      offering: [setOfferingTeamId, setOfferedPlayerIds, offeredPlayerIds],
      receiving: [setReceivingTeamId, setRequestedPlayerIds, requestedPlayerIds],
      third: [setThirdTeamId, setThirdTeamPlayerIds, thirdTeamPlayerIds]
    };
    const [setTeamId, setSelected, selectedIds] = setters[sideKey];

    setTeamId(newTeamId);
    setSelected([]);

    const droppedTeamId = parseInt(previousTeamId, 10);
    setPlayerDestinations(prev => {
      const next = { ...prev };
      selectedIds.forEach(id => { delete next[id]; });
      Object.keys(next).forEach(playerId => {
        if (next[playerId] === droppedTeamId) delete next[playerId];
      });
      return next;
    });
  };

  const addThirdTeam = () => {
    // Die bisherigen Ziele stammen aus dem 2-Team-Fall und sind leer; die
    // Auswahl bleibt bestehen, bekommt aber jetzt Ziele zugewiesen.
    const ringTargets = {};
    offeredPlayerIds.forEach(id => { ringTargets[id] = parseInt(receivingTeamId, 10) || null; });
    requestedPlayerIds.forEach(id => { ringTargets[id] = parseInt(offeringTeamId, 10) || null; });
    setPlayerDestinations(prev => ({ ...prev, ...ringTargets }));
    setThirdTeamEnabled(true);
  };

  const removeThirdTeam = () => {
    setThirdTeamEnabled(false);
    setThirdTeamId('');
    setThirdTeamPlayerIds([]);
    setPlayerDestinations({});
    setThreeTeamValidation(null);
  };

  // Jeder ausgewaehlte Spieler braucht ein Ziel, sonst ist der Trade unvollstaendig.
  const missingDestinations = isThreeTeam
    ? tradeSides.flatMap(side => side.selectedIds.filter(id => !playerDestinations[id]))
    : [];

  const threeTeamReady = isThreeTeam
    && thirdTeamId !== ''
    && participatingTeamIds.length === 3
    && tradeSides.every(side => side.selectedIds.length > 0)
    && missingDestinations.length === 0;

  const getTeamBudget = (teamId) => {
    if (!tradeLimits || !teamId) return null;
    return tradeLimits.teams.find(row => row.teamId === parseInt(teamId, 10)) || null;
  };

  // Does the current selection blow the season budget of either team?
  // A pending offer already reserves the slot, so the check uses the same
  // numbers the server enforces.
  const getBudgetBlockers = () => {
    if (!tradeLimits) return [];
    const maxTrades = tradeLimits.maxTradesPerTeam;
    const maxPlayers = tradeLimits.maxTradedPlayersPerTeam;
    // Bei einem 3-Team-Trade verbrauchen alle drei Teams einen Slot.
    const sides = tradeSides.map(side => ({
      teamId: side.teamId,
      giving: side.selectedIds.length
    }));

    const blockers = [];
    sides.forEach(({ teamId, giving }) => {
      const budget = getTeamBudget(teamId);
      if (!budget || giving === 0) return;
      if (maxTrades !== null && maxTrades !== undefined && budget.trades + 1 > maxTrades) {
        blockers.push({
          key: `${budget.teamId}-trades`,
          text: t('trades.budget.blockedTrades', { team: budget.name, used: budget.trades, max: maxTrades })
        });
      }
      if (maxPlayers !== null && maxPlayers !== undefined && budget.players + giving > maxPlayers) {
        blockers.push({
          key: `${budget.teamId}-players`,
          text: t('trades.budget.blockedPlayers', {
            team: budget.name, used: budget.players, max: maxPlayers, adding: giving
          })
        });
      }
    });
    return blockers;
  };

  const renderBudgetLine = (teamId, giving) => {
    const budget = getTeamBudget(teamId);
    if (!budget) return null;
    const maxTrades = tradeLimits?.maxTradesPerTeam;
    const maxPlayers = tradeLimits?.maxTradedPlayersPerTeam;
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        {t('trades.budget.line', {
          trades: budget.trades,
          maxTrades: maxTrades === null || maxTrades === undefined ? '∞' : maxTrades,
          players: budget.players,
          maxPlayers: maxPlayers === null || maxPlayers === undefined ? '∞' : maxPlayers
        })}
        {giving > 0 && (
          <span className="ml-1">
            {t('trades.budget.afterThisTrade', {
              trades: budget.trades + 1,
              players: budget.players + giving
            })}
          </span>
        )}
      </p>
    );
  };

  const canSeeDiscordText = (trade) => {
    if (user?.role === 'admin') return true;
    return !!user?.teamId && (
      user.teamId === trade.offeringTeamId
      || user.teamId === trade.receivingTeamId
      || user.teamId === trade.thirdTeamId
    );
  };

  const loadDiscordText = async (tradeId) => {
    setDiscordLoadingId(tradeId);
    try {
      const response = await fetch(`/api/trades/${tradeId}/discord-text`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load Discord text');
      setDiscordTexts(prev => ({ ...prev, [tradeId]: data.text }));
      return data.text;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setDiscordLoadingId(null);
    }
  };

  const copyDiscordText = async (tradeId, text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for browsers without the async clipboard API (or http://)
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedTradeId(tradeId);
      setTimeout(() => setCopiedTradeId(current => (current === tradeId ? null : current)), 2500);
    } catch (err) {
      setError(t('trades.discord.copyFailed'));
    }
  };

  const renderDiscordPanel = (trade) => {
    if (!canSeeDiscordText(trade)) return null;
    const text = discordTexts[trade.id];
    return (
      <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-semibold text-sm text-indigo-900 dark:text-indigo-200">
              💬 {t('trades.discord.title')}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">{t('trades.discord.hint')}</p>
          </div>
          <div className="flex gap-2">
            {!text ? (
              <button
                onClick={() => loadDiscordText(trade.id)}
                disabled={discordLoadingId === trade.id}
                className="bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700 disabled:opacity-60"
              >
                {discordLoadingId === trade.id ? t('common.loading') : t('trades.discord.generate')}
              </button>
            ) : (
              <button
                onClick={() => copyDiscordText(trade.id, text)}
                className="bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700"
              >
                {copiedTradeId === trade.id ? `✅ ${t('trades.discord.copied')}` : `📋 ${t('trades.discord.copy')}`}
              </button>
            )}
          </div>
        </div>
        {text && (
          <pre className="mt-3 p-3 bg-white dark:bg-gray-900 border border-indigo-100 dark:border-indigo-900 rounded text-sm whitespace-pre-wrap font-mono text-gray-800 dark:text-gray-200">
{text}
          </pre>
        )}
      </div>
    );
  };

  const getTeamPlayers = (teamId) => {
    return teamPlayers[teamId] || [];
  };

  const getPlayerById = (playerId) => {
    // Search through all team players to find the player
    for (const teamId in teamPlayers) {
      const player = teamPlayers[teamId].find(p => p.id === parseInt(playerId));
      if (player) return player;
    }
    return null;
  };

  const getPlayerFullName = (player) => {
    if (!player) return '';
    return player.name || `${player.firstName} ${player.lastName}`;
  };

  const calculateTotalOVR = (playerIds) => {
    const selectedPlayers = playerIds.map(id => getPlayerById(id)).filter(Boolean);
    if (selectedPlayers.length === 0) return 0;
    const sum = selectedPlayers.reduce((acc, p) => acc + p.overallRating, 0);
    return sum;
  };

  const validateTrade = async () => {
    if (offeredPlayerIds.length === 0 || requestedPlayerIds.length === 0) {
      setValidation({ valid: false, errors: ['Must select players from both sides'] });
      return;
    }

    try {
      const response = await fetch('/api/trades/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offeredPlayerIds, requestedPlayerIds })
      });

      const data = await response.json();
      setValidation(data.validation);
    } catch (err) {
      console.error('Error validating trade:', err);
      setError('Failed to validate trade');
    }
  };

  // Regel 5.2 / 5.2.1 wird bei drei Teams pro Team geprüft — das rechnet der
  // Server, damit UI und Durchsetzung nicht auseinanderlaufen können.
  const validateThreeTeamTrade = async () => {
    try {
      const response = await fetch('/api/trades/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          offeringTeamId: parseInt(offeringTeamId, 10),
          receivingTeamId: parseInt(receivingTeamId, 10),
          thirdTeamId: parseInt(thirdTeamId, 10),
          offeredPlayerIds,
          requestedPlayerIds,
          thirdTeamPlayerIds,
          playerDestinations
        })
      });

      const data = await response.json();
      if (!response.ok) {
        setThreeTeamValidation({ valid: false, errors: [data.error || 'Failed to validate trade'], legs: [] });
        return;
      }
      setThreeTeamValidation(data.validation);
    } catch (err) {
      console.error('Error validating three-team trade:', err);
      setError('Failed to validate trade');
    }
  };

  useEffect(() => {
    if (isThreeTeam) {
      setValidation(null);
      if (threeTeamReady) {
        validateThreeTeamTrade();
      } else {
        setThreeTeamValidation(null);
      }
      return;
    }

    setThreeTeamValidation(null);
    if (offeredPlayerIds.length > 0 && requestedPlayerIds.length > 0) {
      validateTrade();
    } else {
      setValidation(null);
    }
  }, [
    offeredPlayerIds, requestedPlayerIds, thirdTeamPlayerIds,
    playerDestinations, isThreeTeam, threeTeamReady,
    offeringTeamId, receivingTeamId, thirdTeamId
  ]);

  const createTrade = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/trades', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          offeringTeamId: parseInt(offeringTeamId),
          receivingTeamId: parseInt(receivingTeamId),
          offeredPlayerIds,
          requestedPlayerIds,
          message: tradeMessage,
          isPrivate,
          // Nur bei einem 3-Team-Trade gesetzt — der Server erkennt daran,
          // welchen Pfad er nimmt.
          ...(isThreeTeam ? {
            thirdTeamId: parseInt(thirdTeamId, 10),
            thirdTeamPlayerIds,
            playerDestinations
          } : {})
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(t('trades.discord.createdWithText'));
        // Reset form
        setOfferedPlayerIds([]);
        setRequestedPlayerIds([]);
        setThirdTeamPlayerIds([]);
        setThirdTeamEnabled(false);
        setThirdTeamId('');
        setPlayerDestinations({});
        setTradeMessage('');
        setIsPrivate(false);
        setValidation(null);
        setThreeTeamValidation(null);
        fetchTrades();
        fetchTradeLimits();
        setActiveTab('pending');
        // Load the Discord announcement right away so the GM only has to copy
        if (data.trade?.id) {
          loadDiscordText(data.trade.id);
        }
      } else {
        setError(data.error || 'Failed to create trade');
        if (data.validationErrors) {
          setError(data.validationErrors.join(', '));
        }
        if (data.tradeLimit) {
          // Budget was already full on the server (e.g. a parallel offer)
          setError(`${t('trades.budget.blockedTitle')} — ${data.error}`);
          fetchTradeLimits();
        }
      }
    } catch (err) {
      setError('Failed to create trade offer');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const acceptTrade = async (trade) => {
    const threeTeam = !!trade.thirdTeamId;
    const question = threeTeam
      ? t('trades.multi.confirmAccept')
      : 'Are you sure you want to accept this trade? This will immediately swap the players.';
    if (!window.confirm(question)) {
      return;
    }

    try {
      const response = await fetch(`/api/trades/${trade.id}/accept`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Bei drei Teams kann die eigene Zustimmung gespeichert sein, ohne dass
        // der Trade schon ausgefuehrt wurde — es fehlt dann der andere Partner.
        const stillWaiting = (data.awaitingApprovalTeamIds || []).length > 0;
        setSuccess(stillWaiting
          ? t('trades.multi.approvalRecorded')
          : 'Trade accepted and executed!');
        fetchTrades();
        fetchTradeLimits();
        if (!stillWaiting) fetchAllTeamPlayers(); // Refresh players to show updated teams
      } else {
        setError(data.error || 'Failed to accept trade');
      }
    } catch (err) {
      setError('Failed to accept trade');
      console.error(err);
    }
  };

  const withdrawTrade = async (tradeId) => {
    if (!window.confirm('Are you sure you want to withdraw this trade offer?')) {
      return;
    }

    try {
      const response = await fetch(`/api/trades/${tradeId}/withdraw`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Trade offer withdrawn');
        fetchTrades();
        fetchTradeLimits();
      } else {
        setError(data.error || 'Failed to withdraw trade');
      }
    } catch (err) {
      setError('Failed to withdraw trade');
      console.error(err);
    }
  };

  const rejectTrade = async (tradeId) => {
    if (!window.confirm('Are you sure you want to reject this trade?')) {
      return;
    }

    try {
      const response = await fetch(`/api/trades/${tradeId}/reject`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Trade rejected');
        fetchTrades();
        fetchTradeLimits();
      } else {
        setError(data.error || 'Failed to reject trade');
      }
    } catch (err) {
      setError('Failed to reject trade');
      console.error(err);
    }
  };

  // --- Darstellung bestehender Trades -------------------------------------
  const tradeTitle = (trade) => (trade.thirdTeamId
    ? [trade.offeringTeam?.name, trade.receivingTeam?.name, trade.thirdTeam?.name]
        .filter(Boolean).join(' ↔️ ')
    : `${trade.offeringTeam?.name} ↔️ ${trade.receivingTeam?.name}`);

  // Wer darf auf ein Angebot antworten: bei zwei Teams das empfangende, bei
  // drei Teams beide Partner-Teams. Admins immer.
  const canRespondToTrade = (trade) => {
    if (user?.role === 'admin') return true;
    if (user?.role !== 'team_owner' || !user?.teamId) return false;
    const partners = [trade.receivingTeamId, trade.thirdTeamId].filter(Boolean);
    return partners.includes(user.teamId);
  };

  const hasOwnApproval = (trade) => {
    if (!trade.thirdTeamId || !user?.teamId) return false;
    const approvals = trade.approvals || {};
    return !!approvals[String(user.teamId)];
  };

  const ruleLine = (leg) => (leg.isEqual
    ? t('trades.multi.ruleTotal', {
        give: leg.giveTotal, receive: leg.receiveTotal, difference: leg.difference
      })
    : t('trades.multi.ruleAvg', {
        giveTotal: leg.giveTotal, giveCount: leg.giveCount, giveAvg: leg.giveAvg,
        receiveTotal: leg.receiveTotal, receiveCount: leg.receiveCount,
        receiveAvg: leg.receiveAvg, difference: leg.difference
      }));

  /**
   * Ein 3-Team-Trade laesst sich nicht in "bietet an / verlangt" aufteilen —
   * jeder Spieler hat sein eigenes Ziel. Deshalb pro Team ein Block mit
   * Abgabe, Zugang und dem Rechenweg, genau wie im Discord-Text.
   */
  const renderThreeTeamLegs = (trade) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
      {(trade.legs || []).map(leg => (
        <div key={leg.teamId} className="border rounded-lg p-4">
          <h4 className="font-bold mb-2">{leg.name}</h4>
          <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase mb-1">
            {t('trades.multi.givesAway')}
          </p>
          <div className="space-y-1 mb-3">
            {leg.giving.map(player => (
              <div key={player.id} className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm flex justify-between gap-2">
                <span className="truncate">{getPlayerFullName(player)}</span>
                <span className="font-semibold whitespace-nowrap">{player.overallRating}</span>
              </div>
            ))}
          </div>
          <p className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase mb-1">
            {t('trades.multi.receives')}
          </p>
          <div className="space-y-1 mb-3">
            {leg.receiving.map(player => (
              <div key={player.id} className="p-2 bg-green-50 dark:bg-green-900/20 rounded text-sm flex justify-between gap-2">
                <span className="truncate">{getPlayerFullName(player)}</span>
                <span className="font-semibold whitespace-nowrap">{player.overallRating}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">{ruleLine(leg)}</p>
        </div>
      ))}
    </div>
  );

  /**
   * Zustimmungsstand eines 3-Team-Trades. Das anbietende Team stimmt mit dem
   * Anlegen zu, deshalb stehen hier nur die beiden Partner.
   */
  const renderApprovalState = (trade) => {
    if (!trade.thirdTeamId || trade.status !== 'pending') return null;
    const approvals = trade.approvals || {};
    const partners = [
      { teamId: trade.receivingTeamId, name: trade.receivingTeam?.name },
      { teamId: trade.thirdTeamId, name: trade.thirdTeam?.name }
    ].filter(entry => entry.teamId);

    return (
      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          {t('trades.multi.approvalTitle')}
        </p>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
          {partners.map(partner => (
            <li key={partner.teamId}>
              {approvals[String(partner.teamId)] ? '✅' : '⏳'} {partner.name || `Team ${partner.teamId}`}
              {' — '}
              {approvals[String(partner.teamId)]
                ? t('trades.multi.approved')
                : t('trades.multi.awaiting')}
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {t('trades.multi.needsBothPartners')}
        </p>
      </div>
    );
  };

  const budgetBlockers = getBudgetBlockers();

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6">🔄 Trade Management</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 dark:text-red-400">⚠️ {error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 dark:text-green-400">✅ {success}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('create')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'create'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:text-blue-400'
          }`}
        >
          Create Trade Offer
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'pending'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:text-blue-400'
          }`}
        >
          Pending Trades ({trades.filter(t => t.status === 'pending').length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'history'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:text-blue-400'
          }`}
        >
          Trade History
        </button>
      </div>

      {/* Create Trade Tab */}
      {activeTab === 'create' && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg dark:shadow-gray-900">
          <h3 className="text-xl font-bold mb-2">Create New Trade Offer</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            🏀 Using active season rosters - only teams and players from the current active season are shown
          </p>

          {/* Team Selection */}
          <div className={`grid grid-cols-1 gap-6 mb-4 ${isThreeTeam ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Offering Team {user?.role === 'team_owner' ? '(Auto-selected)' : ''}
              </label>
              <select
                value={offeringTeamId}
                onChange={(e) => handleTeamChange('offering', offeringTeamId, e.target.value)}
                className={`w-full p-2 border rounded ${
                  user?.role === 'team_owner' 
                    ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-70' 
                    : 'bg-white dark:bg-gray-800'
                }`}
                disabled={user?.role === 'team_owner'} // Disabled for team owners
              >
                <option value="">Select team...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
              {user?.role === 'team_owner' && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Team owners can only create trades for their own team
                </p>
              )}
              {renderBudgetLine(offeringTeamId, offeredPlayerIds.length)}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Receiving Team (Teams with GMs only)
              </label>
              <select
                value={receivingTeamId}
                onChange={(e) => handleTeamChange('receiving', receivingTeamId, e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="">Select team...</option>
                {receivingTeams.length > 0 ? (
                  receivingTeams
                    .filter(t => t.id !== parseInt(offeringTeamId) && t.id !== parseInt(thirdTeamId))
                    .map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} (GM: {gmDisplayName(team.owner)})
                      </option>
                    ))
                ) : (
                  <option value="" disabled>No teams with GMs available</option>
                )}
              </select>
              {renderBudgetLine(receivingTeamId, requestedPlayerIds.length)}
            </div>

            {/* Drittes Team: nur sichtbar, wenn der GM es dazugeholt hat */}
            {isThreeTeam && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('trades.multi.thirdTeamLabel')}
                  </label>
                  <button
                    type="button"
                    onClick={removeThirdTeam}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline"
                  >
                    {t('trades.multi.removeThirdTeam')}
                  </button>
                </div>
                <select
                  value={thirdTeamId}
                  onChange={(e) => handleTeamChange('third', thirdTeamId, e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="">{t('trades.multi.selectTeam')}</option>
                  {receivingTeams
                    .filter(t => t.id !== parseInt(offeringTeamId) && t.id !== parseInt(receivingTeamId))
                    .map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} (GM: {gmDisplayName(team.owner)})
                      </option>
                    ))}
                </select>
                {renderBudgetLine(thirdTeamId, thirdTeamPlayerIds.length)}
              </div>
            )}
          </div>

          {/* 3-Team-Trade dazuholen */}
          {!isThreeTeam && (
            <div className="mb-6">
              <button
                type="button"
                onClick={addThirdTeam}
                disabled={!offeringTeamId || !receivingTeamId}
                className="bg-purple-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {t('trades.multi.addThirdTeam')}
              </button>
            </div>
          )}

          {isThreeTeam && (
            <div className="mb-6 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
              <p className="text-sm text-purple-900 dark:text-purple-200">
                🔀 {t('trades.multi.hint')}
              </p>
            </div>
          )}

          {/* Player Selection */}
          {offeringTeamId && receivingTeamId && (
            <div className={`grid grid-cols-1 gap-6 mb-6 ${isThreeTeam ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              {tradeSides.map(side => {
                const sidePlayers = getTeamPlayers(side.teamId);
                const otherTeamIds = participatingTeamIds.filter(
                  id => id !== parseInt(side.teamId, 10)
                );
                const accentSelected = {
                  blue: 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500',
                  green: 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500',
                  purple: 'bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500'
                }[side.accent];

                return (
                  <div key={side.key} className="border rounded-lg p-4">
                    <h4 className="font-bold mb-2">
                      {isThreeTeam
                        ? t('trades.multi.playersFrom', {
                            team: getTeamName(side.teamId) || '–',
                            count: side.selectedIds.length,
                            max: 4
                          })
                        : (side.key === 'offering'
                            ? `Players to Offer (${side.selectedIds.length}/4)`
                            : `Players to Request (${side.selectedIds.length}/4)`)}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Total OVR: {calculateTotalOVR(side.selectedIds)}
                    </p>

                    {/* Zielteam je ausgewähltem Spieler — nur bei drei Teams nötig */}
                    {isThreeTeam && side.selectedIds.length > 0 && (
                      <div className="mb-3 space-y-2">
                        {side.selectedIds.map(playerId => {
                          const player = getPlayerById(playerId);
                          return (
                            <div key={playerId} className="flex items-center gap-2 text-sm">
                              <span className="flex-1 truncate">{getPlayerFullName(player)}</span>
                              <span className="text-gray-500 dark:text-gray-400">
                                {t('trades.multi.targetTeam')}
                              </span>
                              <select
                                value={playerDestinations[playerId] || ''}
                                onChange={(e) => setDestination(playerId, e.target.value)}
                                className={`p-1 border rounded text-sm ${
                                  playerDestinations[playerId] ? '' : 'border-red-400'
                                }`}
                              >
                                <option value="">{t('trades.multi.noTargetYet')}</option>
                                {otherTeamIds.map(teamId => (
                                  <option key={teamId} value={teamId}>
                                    {getTeamName(teamId)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {sidePlayers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p>⚠️ No players found for this team in active season</p>
                        <p className="text-sm">This team may not have an active roster</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {sidePlayers.map(player => (
                          <div
                            key={player.id}
                            onClick={() => toggleSelection(side, player.id)}
                            className={`p-3 rounded cursor-pointer ${
                              side.selectedIds.includes(player.id)
                                ? accentSelected
                                : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:bg-gray-700 border-2 border-transparent'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{getPlayerFullName(player)}</span>
                              <span className="text-sm bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded">
                                {player.overallRating} OVR
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {player.position} | {player.height} | {player.archetypeName}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 3-Team-Bilanz je Team (Regel 5.2 / 5.2.1) */}
          {isThreeTeam && threeTeamValidation && (
            <div className={`mb-4 p-4 rounded-lg ${
              threeTeamValidation.valid
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`font-semibold ${threeTeamValidation.valid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {threeTeamValidation.valid
                  ? `✅ ${t('trades.multi.valid')}`
                  : `❌ ${t('trades.multi.invalid')}`}
              </p>
              <div className="mt-3 space-y-3">
                {(threeTeamValidation.legs || []).map(leg => (
                  <div key={leg.teamId} className="text-sm text-gray-700 dark:text-gray-300">
                    <p className="font-semibold">{leg.name}</p>
                    <p>
                      <span className="text-red-700 dark:text-red-400">{t('trades.multi.givesAway')}:</span>{' '}
                      {leg.giving.map(p => `${getPlayerFullName(p)} (${p.overallRating})`).join(', ') || '–'}
                    </p>
                    <p>
                      <span className="text-green-700 dark:text-green-400">{t('trades.multi.receives')}:</span>{' '}
                      {leg.receiving.map(p => `${getPlayerFullName(p)} (${p.overallRating})`).join(', ') || '–'}
                    </p>
                    <p className={leg.withinLimit ? 'text-gray-600 dark:text-gray-400' : 'text-red-700 dark:text-red-400 font-semibold'}>
                      {leg.isEqual
                        ? t('trades.multi.ruleTotal', {
                            give: leg.giveTotal,
                            receive: leg.receiveTotal,
                            difference: leg.difference
                          })
                        : t('trades.multi.ruleAvg', {
                            giveTotal: leg.giveTotal,
                            giveCount: leg.giveCount,
                            giveAvg: leg.giveAvg,
                            receiveTotal: leg.receiveTotal,
                            receiveCount: leg.receiveCount,
                            receiveAvg: leg.receiveAvg,
                            difference: leg.difference
                          })}
                    </p>
                  </div>
                ))}
              </div>
              {threeTeamValidation.errors && threeTeamValidation.errors.length > 0 && (
                <ul className="mt-2 text-sm text-red-700 dark:text-red-400 list-disc list-inside">
                  {threeTeamValidation.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {isThreeTeam && !threeTeamReady && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded text-sm text-yellow-800 dark:text-yellow-300">
              ℹ️ {t('trades.multi.incomplete')}
            </div>
          )}
          {/* Validation Info (2-Team-Trade) */}
          {!isThreeTeam && validation && (
            <div className={`mb-4 p-4 rounded-lg ${
              validation.valid
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`font-semibold ${validation.valid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {validation.valid ? '✅ Trade is valid!' : '❌ Trade validation failed'}
              </p>
              {validation.ovrDifference !== undefined && (
                <div className="text-sm mt-2 text-gray-700 dark:text-gray-300">
                  {validation.isEqualTrade ? (
                    <>
                      <p>Offered Total OVR: {validation.offeredTotalOVR}</p>
                      <p>Requested Total OVR: {validation.requestedTotalOVR}</p>
                      <p>OVR Difference (total): {validation.ovrDifference} (max: 3)</p>
                    </>
                  ) : (
                    <>
                      <p>Offered Avg OVR: {validation.offeredAvgOVR} (total: {validation.offeredTotalOVR})</p>
                      <p>Requested Avg OVR: {validation.requestedAvgOVR} (total: {validation.requestedTotalOVR})</p>
                      <p>OVR Difference (avg): {validation.ovrDifference} (max: 3)</p>
                    </>
                  )}
                </div>
              )}
              {validation.errors && validation.errors.length > 0 && (
                <ul className="mt-2 text-sm text-red-700 dark:text-red-400 list-disc list-inside">
                  {validation.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Trade Message (Optional)
            </label>
            <textarea
              value={tradeMessage}
              onChange={(e) => setTradeMessage(e.target.value)}
              className="w-full p-2 border rounded"
              rows="3"
              placeholder="Add a message to the receiving team..."
            />
          </div>

          {/* Private Trade Toggle */}
          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="w-5 h-5 text-blue-600 dark:text-blue-400 rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-semibold text-gray-700 dark:text-gray-300">🔒 Private Trade</span>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Only you and the other GM will see this trade. It becomes public only after it is accepted.
                </p>
              </div>
            </label>
          </div>

          {/* Season trade budget */}
          {budgetBlockers.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="font-semibold text-red-800 dark:text-red-300 mb-1">
                🚫 {t('trades.budget.blockedTitle')}
              </p>
              <ul className="text-sm text-red-700 dark:text-red-400 list-disc list-inside">
                {budgetBlockers.map(blocker => (
                  <li key={blocker.key}>{blocker.text}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={createTrade}
            disabled={
              loading
              || !offeringTeamId
              || !receivingTeamId
              || budgetBlockers.length > 0
              || (isThreeTeam
                ? (!threeTeamReady || !threeTeamValidation?.valid)
                : !validation?.valid)
            }
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Trade...' : 'Create Trade Offer'}
          </button>

          {/* Trade Rules */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h4 className="font-bold mb-2">📋 Trade Rules:</h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              <li>• Maximum 4 players per side</li>
              <li>• Max ±3 OVR total difference between both sides</li>
              <li>• {t('trades.multi.rule')}</li>
              {tradeLimits && (
                <li>• {t('trades.budget.rule', {
                  maxTrades: tradeLimits.maxTradesPerTeam === null || tradeLimits.maxTradesPerTeam === undefined
                    ? '∞' : tradeLimits.maxTradesPerTeam,
                  maxPlayers: tradeLimits.maxTradedPlayersPerTeam === null || tradeLimits.maxTradedPlayersPerTeam === undefined
                    ? '∞' : tradeLimits.maxTradedPlayersPerTeam
                })}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Pending Trades Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {trades.filter(t => t.status === 'pending').length === 0 ? (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow text-center text-gray-500 dark:text-gray-400">
              No pending trades
            </div>
          ) : (
            trades.filter(t => t.status === 'pending').map(trade => (
              <div key={trade.id} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg dark:shadow-gray-900">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold">
                      {tradeTitle(trade)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Created: {new Date(trade.createdAt).toLocaleString()}
                      {trade.thirdTeamId && (
                        <span className="ml-2 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-2 py-0.5 rounded-full text-xs font-semibold">
                          🔀 {t('trades.multi.threeTeamBadge')}
                        </span>
                      )}
                    </p>
                  </div>
                  <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                    Pending
                  </span>
                  {trade.isPrivate && (
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold ml-2">
                      🔒 Private
                    </span>
                  )}
                </div>

                {trade.thirdTeamId ? renderThreeTeamLegs(trade) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  {/* Offered Players */}
                  <div>
                    <h4 className="font-bold mb-2">
                      Offered by {trade.offeringTeam?.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Avg OVR: {trade.offeredPlayersAvgOVR?.toFixed(1)}
                    </p>
                    <div className="space-y-2">
                      {trade.offeredPlayers?.map(player => (
                        <div key={player.id} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                          <div className="flex justify-between">
                            <span className="font-medium">{getPlayerFullName(player)}</span>
                            <span className="text-sm bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded">
                              {player.overallRating} OVR
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {player.position} | {player.archetypeName}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Requested Players */}
                  <div>
                    <h4 className="font-bold mb-2">
                      Requested from {trade.receivingTeam?.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Avg OVR: {trade.requestedPlayersAvgOVR?.toFixed(1)}
                    </p>
                    <div className="space-y-2">
                      {trade.requestedPlayers?.map(player => (
                        <div key={player.id} className="p-3 bg-green-50 rounded">
                          <div className="flex justify-between">
                            <span className="font-medium">{getPlayerFullName(player)}</span>
                            <span className="text-sm bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded">
                              {player.overallRating} OVR
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {player.position} | {player.archetypeName}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                )}

                {renderApprovalState(trade)}

                {trade.message && (
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <p className="text-sm text-gray-700 dark:text-gray-300">💬 {trade.message}</p>
                  </div>
                )}

                {renderDiscordPanel(trade)}

                {/* Antworten darf jedes Partner-Team (bei drei Teams beide) und der Admin */}
                {canRespondToTrade(trade) && (
                  <div className="flex gap-4">
                    <button
                      onClick={() => acceptTrade(trade)}
                      disabled={hasOwnApproval(trade)}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {hasOwnApproval(trade)
                        ? `✅ ${t('trades.multi.alreadyApproved')}`
                        : '✅ Accept Trade'}
                    </button>
                    <button
                      onClick={() => rejectTrade(trade.id)}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                    >
                      ❌ Reject Trade
                    </button>
                  </div>
                )}

                {/* Show withdraw button and message to offering team owner */}
                {user?.role === 'team_owner' && user?.teamId === trade.offeringTeamId && (
                  <div className="flex flex-col gap-3">
                    <div className="p-3 bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded text-sm text-yellow-800">
                      ℹ️ Waiting for {trade.thirdTeamId
                        ? [trade.receivingTeam?.name, trade.thirdTeam?.name].filter(Boolean).join(' & ')
                        : trade.receivingTeam?.name} to respond to your trade offer
                    </div>
                    <button
                      onClick={() => withdrawTrade(trade.id)}
                      className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 text-sm"
                    >
                      ↩️ Withdraw Trade Offer
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Trade History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="mb-4">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mr-2">Filter by status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="p-2 border rounded"
            >
              <option value="">All</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </div>

          {trades.filter(t => t.status !== 'pending').length === 0 ? (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow text-center text-gray-500 dark:text-gray-400">
              No trade history
            </div>
          ) : (
            trades.filter(t => t.status !== 'pending').map(trade => (
              <div key={trade.id} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold">
                      {tradeTitle(trade)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(trade.processedAt || trade.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    trade.status === 'accepted' ? 'bg-green-100 dark:bg-green-900/30 text-green-800' :
                    trade.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 text-red-800' :
                    trade.status === 'withdrawn' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800' :
                    'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }`}>
                    {trade.status.charAt(0).toUpperCase() + trade.status.slice(1)}
                  </span>
                </div>

                {trade.thirdTeamId ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {(trade.legs || []).map(leg => (
                      <div key={leg.teamId}>
                        <p className="font-semibold">{leg.name}</p>
                        <p className="text-red-700 dark:text-red-400">
                          {t('trades.multi.givesAway')}:{' '}
                          {leg.giving.map(p => `${getPlayerFullName(p)} (${p.overallRating})`).join(', ') || '–'}
                        </p>
                        <p className="text-green-700 dark:text-green-400">
                          {t('trades.multi.receives')}:{' '}
                          {leg.receiving.map(p => `${getPlayerFullName(p)} (${p.overallRating})`).join(', ') || '–'}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold">Offered:</p>
                    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                      {trade.offeredPlayers?.map(p => (
                        <li key={p.id}>{getPlayerFullName(p)} ({p.overallRating} OVR)</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold">For:</p>
                    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                      {trade.requestedPlayers?.map(p => (
                        <li key={p.id}>{getPlayerFullName(p)} ({p.overallRating} OVR)</li>
                      ))}
                    </ul>
                  </div>
                </div>
                )}

                {trade.status === 'accepted' && renderDiscordPanel(trade)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TradeManagement;
